import { API_BASE_URL } from "src/config/apiBase";
import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { getDecryptedUserFromCookie } from "../utils/getCookie";
import { decryptToken } from "../utils/getToken";
import Select from "react-select";
import * as Icons from "lucide-react/dist/esm/icons"; // ✅ import all safely

const iconKeys = Object.keys(Icons);
const iconOptions = iconKeys.map((iconName) => {
    const IconComp = Icons[iconName];
    if (!IconComp) return null; // skip undefined ones
    return {
        value: iconName,
        label: (
            <div className="flex items-center gap-2">
                <IconComp size={18} />
                <span>{iconName}</span>
            </div>
        ),
    };
}).filter(Boolean);



const CallToActionFirstModal = ({ isOpen, setIsOpen, data, setData, sliderFetch }) => {
    const url = API_BASE_URL;
    const token = decryptToken("accessToken") || "";
    const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");
    const [load, setLoad] = useState(false);

    // 🔹 Prepare Lucide icons as options for react-select
    // const iconOptions = Object.keys(Icons).map((iconName) => ({
    //     value: iconName,
    //     label: (
    //         <div className="flex items-center gap-2">
    //             {React.createElement(Icons[iconName], { size: 18 })}
    //             <span>{iconName}</span>
    //         </div>
    //     ),
    // }));

    const handleChange = (e) => {
        setData({ ...data, [e.target.name]: e.target.value });
    };

    // Handle Add / Remove icon-label pairs
    const handleAddIconLabel = () => {
        const newList = [...(data.iconLabelPairs || []), { icon: "", label: "" }];
        setData({ ...data, iconLabelPairs: newList });
    };

    const handleRemoveIconLabel = (index) => {
        const newList = data.iconLabelPairs.filter((_, i) => i !== index);
        setData({ ...data, iconLabelPairs: newList });
    };

    const handleLabelChange = (index, value) => {
        const newList = [...data.iconLabelPairs];
        newList[index].label = value;
        setData({ ...data, iconLabelPairs: newList });
    };

    // ✅ Handle icon select (save icon name)
    const handleIconSelect = (index, selectedOption) => {
        const newList = [...data.iconLabelPairs];
        newList[index].icon = selectedOption?.value || "";
        setData({ ...data, iconLabelPairs: newList });
    };

    // Dynamic text + link pairs
    const handleAddOption = () => {
        const newOptions = [...(data.options || []), { text: "", link: "" }];
        setData({ ...data, options: newOptions });
    };

    const handleOptionChange = (index, field, value) => {
        const updatedOptions = [...data.options];
        updatedOptions[index][field] = value;
        setData({ ...data, options: updatedOptions });
    };

    const handleRemoveOption = (index) => {
        const updatedOptions = data.options.filter((_, i) => i !== index);
        setData({ ...data, options: updatedOptions });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const payload = {
            ...data,
            user_id: isLoggedIn.main_id,
            user_name: isLoggedIn.full_name,
        };
        setLoad(true);
        try {
            const response = await fetch(`${url}/calltoactionfirst${data.id ? `/${data.id}` : ""}`, {
                method: data.id ? "PUT" : "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                toast.success("Call To Action section saved successfully");
                sliderFetch();
                setIsOpen(false);
            } else {
                toast.error("Failed to save Call To Action section");
            }
        } catch (error) {
            console.error(error);
            toast.error("Server error occurred");
        } finally{
            setLoad(false);
        }
    };


    useEffect(() => {
        setData((prev) => ({
            ...prev,
            iconLabelPairs: prev.iconLabelPairs || [],
            options: prev.options || [],
        }));
    }, []);

    if (!isOpen) return null;

    return (
        <div className="custom-modal-overlay" onClick={() => setIsOpen(false)}>
            <div
                className="custom-modal-content max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <h2 className="oms-modal-title">Call To Action Section</h2>

                <form className="oms-modal-form" onSubmit={handleSubmit}>
                    {/* Title */}
                    <div className="oms-form-group">
                        <label className="oms-form-label">Section Title</label>
                        <input
                            type="text"
                            name="title"
                            value={data?.title || ""}
                            onChange={handleChange}
                            placeholder="e.g. Request a Callback"
                        />
                    </div>

                    {/* Button Text */}
                    <div className="oms-form-group">
                        <label className="oms-form-label">Button Text</label>
                        <input
                            type="text"
                            name="button_text"
                            value={data?.button_text || ""}
                            onChange={handleChange}
                            placeholder="e.g. Request a Callback"
                        />
                    </div>

                    {/* 🔹 Dynamic Icon + Label Pairs */}
                    <div className="oms-form-group">
                        <label className="oms-form-label">Benefits</label>
                        {(data.iconLabelPairs || []).map((item, index) => {
                            const SelectedIcon = Icons[item.icon] || null;
                            return (
                                <div key={index} className="flex items-center gap-3 mb-3">
                                    {SelectedIcon && (
                                        <SelectedIcon size={24} className="text-gray-700" />
                                    )}

                                    <div className="flex-1 min-w-[180px]">
                                        <Select
                                            options={iconOptions}
                                            value={
                                                item.icon
                                                    ? {
                                                        value: item.icon,
                                                        label: (
                                                            <div className="flex items-center gap-2">
                                                                {React.createElement(Icons[item.icon], { size: 18 })}
                                                                <span>{item.icon}</span>
                                                            </div>
                                                        ),
                                                    }
                                                    : null
                                            }
                                            onChange={(selectedOption) =>
                                                handleIconSelect(index, selectedOption)
                                            }
                                            placeholder="Choose Icon"
                                            isClearable
                                        />
                                    </div>

                                    <input
                                        type="text"
                                        placeholder="Label text (e.g. 24/7 Support)"
                                        value={item.label}
                                        onChange={(e) => handleLabelChange(index, e.target.value)}
                                    />

                                    <button
                                        type="button"
                                        className="oms-btn-danger"
                                        onClick={() => handleRemoveIconLabel(index)}
                                    >
                                        ✕
                                    </button>
                                </div>
                            );
                        })}
                        <button
                            type="button"
                            className="oms-btn-outline"
                            onClick={handleAddIconLabel}
                        >
                            + Add 
                        </button>
                    </div>

                    {/* Additional Text + Link Pairs */}
                    <div className="oms-form-group">
                        <label className="oms-form-label">Additional Links</label>
                        {(data?.options || []).map((opt, index) => (
                            <div key={index} className="flex items-center gap-2 mb-2">
                                <input
                                    type="text"
                                    placeholder="Text (e.g. Learn More)"
                                    value={opt.text}
                                    onChange={(e) => handleOptionChange(index, "text", e.target.value)}
                                />
                                <input
                                    type="text"
                                    placeholder="Link (e.g. /learn-more)"
                                    value={opt.link}
                                    onChange={(e) => handleOptionChange(index, "link", e.target.value)}
                                />
                                <button
                                    type="button"
                                    className="oms-btn-danger"
                                    onClick={() => handleRemoveOption(index)}
                                >
                                    ✕
                                </button>
                            </div>
                        ))}
                        <button type="button" className="oms-btn-outline" onClick={handleAddOption}>
                            + Add
                        </button>
                    </div>

                    {/* Actions */}
                    <div className="oms-form-actions">
                        <button type="button" onClick={() => setIsOpen(false)}>
                            Cancel
                        </button>
                        {
                            load ?<button type="button">Saving...</button>:
                            <button type="submit">Save</button>
                        }
                        
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CallToActionFirstModal;
